import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Loader2, Trash2, LogOut, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const loadEnquiries = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/enquiries`, { withCredentials: true });
    setEnquiries(data.enquiries);
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        setUser(r.data);
        loadEnquiries().catch(() => {});
      })
      .catch(() => setUser(false));
  }, [loadEnquiries]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      await loadEnquiries();
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
    setEnquiries([]);
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/enquiries/${id}`, { withCredentials: true });
      setEnquiries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="animate-spin text-primary" size={28} />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-sm">
          <Link to="/" data-testid="admin-login-brand" className="font-syne font-bold text-xl tracking-tight">
            SIVIC<span className="text-primary">.</span>
          </Link>
          <h1 className="font-syne font-bold text-3xl tracking-tight mt-8 mb-2">Admin</h1>
          <p className="text-sm text-muted-foreground mb-10">Sign in to view enquiries.</p>
          <form onSubmit={login} className="space-y-8" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                Email
              </label>
              <input
                id="admin-email"
                data-testid="admin-login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-0 border-b border-border focus:border-primary outline-none py-3 text-base rounded-none transition-colors duration-300"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                Password
              </label>
              <input
                id="admin-password"
                data-testid="admin-login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border-0 border-b border-border focus:border-primary outline-none py-3 text-base rounded-none transition-colors duration-300"
              />
            </div>
            {error && (
              <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-button"
              className="w-full bg-foreground text-white py-4 text-sm font-medium hover:bg-primary transition-colors duration-300 active:scale-[0.99] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={15} className="animate-spin" />}
              Sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      <header className="border-b border-border">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-brand-link" className="font-syne font-bold text-lg tracking-tight">
              SIVIC<span className="text-primary">.</span>
            </Link>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">/ Enquiries</span>
          </div>
          <div className="flex items-center gap-6">
            <span className="text-sm text-muted-foreground hidden sm:block" data-testid="admin-user-email">{user.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors duration-300"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-syne font-bold text-2xl tracking-tight">Enquiries</h1>
          <span
            className="text-xs font-medium uppercase tracking-[0.15em] bg-muted px-3 py-1.5"
            data-testid="admin-enquiry-count"
          >
            {enquiries.length} total
          </span>
        </div>
        {enquiries.length === 0 ? (
          <div className="border border-border py-24 flex flex-col items-center gap-4 text-muted-foreground" data-testid="admin-empty-state">
            <Inbox size={32} strokeWidth={1.5} />
            <p className="text-sm">No enquiries yet. They will appear here as they arrive.</p>
          </div>
        ) : (
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-enquiries-table">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-left">
                  <th className="px-4 py-3 text-xs uppercase tracking-[0.15em] font-medium text-muted-foreground whitespace-nowrap">Date</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-[0.15em] font-medium text-muted-foreground">Name</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-[0.15em] font-medium text-muted-foreground">Company</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-[0.15em] font-medium text-muted-foreground">Email</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-[0.15em] font-medium text-muted-foreground whitespace-nowrap">Budget</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-[0.15em] font-medium text-muted-foreground">Message</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {enquiries.map((e) => (
                  <tr key={e.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors" data-testid={`admin-enquiry-row-${e.id}`}>
                    <td className="px-4 py-4 whitespace-nowrap text-muted-foreground">
                      {new Date(e.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-4 py-4 font-medium">{e.name}</td>
                    <td className="px-4 py-4 text-muted-foreground">{e.company || "—"}</td>
                    <td className="px-4 py-4">
                      <a href={`mailto:${e.email}`} className="text-primary hover:underline">{e.email}</a>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">{e.budget || "—"}</td>
                    <td className="px-4 py-4 min-w-[260px] max-w-md">
                      <p className="text-muted-foreground whitespace-pre-wrap break-words">{e.message}</p>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => remove(e.id)}
                        data-testid={`admin-delete-enquiry-${e.id}`}
                        className="text-muted-foreground hover:text-red-600 transition-colors"
                        aria-label="Delete enquiry"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
